import { DrawAction } from '@engoo/realtime-protocol/draw';
import { TwoParamCommandCodec } from '@engoo/realtime-protocol/draw/trace';
import { makeInt } from 'app/utils/ioTs';
import { traceToIntPoints, intPointsToTrace, isTraceAction, IIntPoint } from 'app/models/rtc/draw/trace';
import isEqual from 'lodash/isEqual';

// import just for types
import simplifyjs from 'simplify-js';

export type SimplifyJS = typeof simplifyjs;

/**
 * Options for `simplify` function
 *
 * Allows for injecting SimplifyJS implementation instead of it
 * being statically imported. Thanks to this loading the actual
 * implementation can be lazily done in `DrawContext` when the
 * document is activated.
 */
export interface ISimplifyDeps {
  /**
   * (Hopefully) dynamically loaded implementation for SimplifyJS
   */
  simplifyJSImpl: SimplifyJS;
  /**
   * Amount of simplification in the same metric as the point
   * coordinates
   */
  tolerance: number;
}

/**
 * Remove duplicate adjacent points
 *
 * Mostly works around the observation that SimplifyJS will output
 * two identical points when the path could really just be one point.
 */
const removeDuplicates = (points: IIntPoint[]): IIntPoint[] => points.reduce<IIntPoint[]>((acc, point) => {
  if (acc.length === 0) {
    return [...acc, point];
  }

  const prev = acc[acc.length - 1];
  if (isEqual(prev, point)) {
    return acc;
  } else {
    return [...acc, point];
  }
}, []);

/**
 * Simplify paths using provided SimplifyJS implementation
 */
const simplify = ({ simplifyJSImpl, tolerance }: ISimplifyDeps, action: DrawAction): DrawAction => {
  if (isTraceAction(action)) {
    const firstCmd = action.trace[0]?.[0];

    if (!TwoParamCommandCodec.is(firstCmd)) {
      return action;
    }

    const points = traceToIntPoints(action.trace);
    const simplifiedPoints = simplifyJSImpl(points, tolerance);

    const trace = intPointsToTrace(
      firstCmd,
      removeDuplicates(simplifiedPoints.map(({ x, y }) => ({
        x: makeInt(Math.round(x)),
        y: makeInt(Math.round(y)),
      }))),
    );

    return {
      ...action,
      trace,
    };
  } else {
    return action;
  }
};

export default simplify;
